// Custom Hook for Grill The Grid Data
import { useState, useEffect, useCallback, useMemo } from 'react';
import { loadCSV } from '../utils/csvLoader';

/**
 * Custom hook for loading Grill The Grid data
 * @returns {Object} - Grill the grid data, loading state, error state, and utility functions
 */
export const useGrillTheGrid = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await loadCSV('/datasets/F1Data/grill_the_grid.csv');
      setData(result);
    } catch (err) {
      setError(err.message || 'Failed to load Grill The Grid data');
      console.error('Grill The Grid loading error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const refetch = useCallback(() => {
    loadData();
  }, [loadData]);

  // Get data grouped by year
  const dataByYear = useMemo(() => {
    if (!data) return {};
    
    const grouped = {};
    data.forEach(row => {
      const year = row.season;
      if (!grouped[year]) {
        grouped[year] = [];
      }
      grouped[year].push(row);
    });
    
    return grouped;
  }, [data]);

  // Get data for a specific year
  const getDataByYear = useCallback((year) => {
    return dataByYear[year] || [];
  }, [dataByYear]);

  // Get data for a specific driver
  const getDataByDriver = useCallback((driverId) => {
    if (!data) return [];
    return data.filter(row => row.driver_id == driverId);
  }, [data]);

  // Get data for a specific driver and year
  const getDataByDriverAndYear = useCallback((driverId, year) => {
    if (!data) return [];
    return data.filter(row => row.driver_id == driverId && row.season == year);
  }, [data]);

  // Get available years
  const availableYears = useMemo(() => {
    if (!data) return [];
    const years = [...new Set(data.map(row => row.season))];
    return years.sort((a, b) => b - a);
  }, [data]);

  // Get driver standings for a year (sorted by total points)
  const getStandingsByYear = useCallback((year) => {
    const yearData = getDataByYear(year);
    const standings = {};
    
    yearData.forEach(row => {
      const driverId = row.driver_id;
      if (!standings[driverId]) {
        standings[driverId] = {
          driverId: driverId,
          totalPoints: 0,
          rounds: {}
        };
      }
      standings[driverId].rounds[row.round] = row.points;
      if (row.points) {
        standings[driverId].totalPoints += parseFloat(row.points) || 0;
      }
    });
    
    const sortedStandings = Object.values(standings).sort((a, b) => b.totalPoints - a.totalPoints);
    
    // Add position (1st, 2nd, 3rd, etc.)
    return sortedStandings.map((standing, index) => ({
      ...standing,
      position: index + 1
    }));
  }, [getDataByYear]);

  return {
    data,
    dataByYear,
    loading,
    error,
    refetch,
    getDataByYear,
    getDataByDriver,
    getDataByDriverAndYear,
    availableYears,
    getStandingsByYear
  };
};
